
/**
 * Module dependencies.
 */

import { ReactNode } from 'react';
import { Route, match } from 'react-router-dom';
import { RouterLink } from './links/router-link';
import { color, media } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import styled, { css } from 'styled-components';

/**
 * `TabProps` type.
 */

type TabProps = {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  exact?: boolean;
  path: string | {
    pathname: string;
    state?: any;
  };
};

/**
 * Export `Tabs` styled component.
 */

export const Tabs = styled.div.attrs({ role: 'tablist' })`
  border-bottom: 1px solid ${color('gray400')};
  display: flex;
  font-size: 14px;
  overflow-x: auto;
  overflow-y: hidden;

  ${media.min('sm')`
    font-size: 16px;
  `}
`;

/**
 * `Link` styled component.
 */

const Link = styled(RouterLink)<{ disabled?: boolean; isSelected: boolean }>`
  color: ${color('secondary')};
  padding: 12px 4px;
  position: relative;
  text-decoration: none;
  transition: color ${theme('animations.defaultTransition')};
  white-space: nowrap;

  ${ifProp('isSelected', css`
    color: ${color('primary')};
  `)}

  &:hover,
  &:focus {
    color: ${color('primary')};

    ${ifProp('isSelected', css`
      color: ${color('blue700')};
    `)}
  }

  &::before {
    background-color: ${color('primary')};
    bottom: -1px;
    content: '';
    height: 2px;
    left: 0;
    position: absolute;
    right: 0;
    transform: scaleX(${ifProp('isSelected', 1, 0)});
    transition: transform ${theme('animations.fastTransition')};
  }

  ${ifProp('disabled', css`
    cursor: default;
    opacity: 0.5;
    pointer-events: none;
  `)}

  &:not(:first-child) {
    margin-left: 16px;
  }
`;

/**
 * Export `Tab` component.
 */

export function Tab(props: TabProps): JSX.Element {
  const { children, disabled, exact = true, path, ...rest } = props;

  return (
    <Route
      exact={exact}
      path={typeof path === 'string' ? path : path.pathname}
    >
      {({ match }: { match: match<{ [x: string]: string | undefined }> | null }) => (
        <Link
          {...rest}
          aria-disabled={disabled}
          aria-selected={!!match}
          disabled={disabled}
          isSelected={!!match}
          role={'tab'}
          to={disabled ? null : path}
        >
          {children}
        </Link>
      )}
    </Route>
  );
}
